import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { isValidDomain } from '../../lib/utils';
import type { Site, CreateSiteDto, UpdateSiteDto } from '../../types';

interface SiteFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  site?: Site | null;
  onSubmit: (data: CreateSiteDto | UpdateSiteDto) => Promise<void>;
  loading?: boolean;
}

export function SiteForm({ open, onOpenChange, site, onSubmit, loading }: SiteFormProps) {
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [allowedOrigins, setAllowedOrigins] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = !!site;

  useEffect(() => {
    if (site) {
      setName(site.name);
      setDomain(site.domain);
      setIsActive(site.is_active);
      setAllowedOrigins(site.allowed_origins?.join(', ') || '');
    } else {
      setName('');
      setDomain('');
      setIsActive(true);
      setAllowedOrigins('');
    }
    setErrors({});
  }, [site, open]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Введіть назву сайту';
    }

    if (!isEditing) {
      if (!domain.trim()) {
        newErrors.domain = 'Введіть домен';
      } else if (!isValidDomain(domain)) {
        newErrors.domain = 'Невірний формат домену';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const origins = allowedOrigins
      .split(',')
      .map((o) => o.trim())
      .filter(Boolean);

    if (isEditing) {
      await onSubmit({
        name,
        is_active: isActive,
        allowed_origins: origins,
      } as UpdateSiteDto);
    } else {
      await onSubmit({
        name,
        domain,
        allowed_origins: origins,
      } as CreateSiteDto);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Редагувати сайт' : 'Додати новий сайт'}</DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Змініть налаштування вашого сайту'
                : 'Додайте новий сайт для інтеграції Captcha 2.0'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Назва сайту</Label>
              <Input
                id="name"
                placeholder="Мій сайт"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={errors.name ? 'border-red-500' : ''}
                disabled={loading}
              />
              {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="domain">Домен</Label>
              <Input
                id="domain"
                placeholder="example.com"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                className={errors.domain ? 'border-red-500' : ''}
                disabled={loading || isEditing}
              />
              {errors.domain && <p className="text-xs text-red-500">{errors.domain}</p>}
              {isEditing && (
                <p className="text-xs text-muted-foreground">
                  Домен не можна змінити після створення
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="origins">Дозволені origins (опціонально)</Label>
              <Input
                id="origins"
                placeholder="https://example.com, https://sub.example.com"
                value={allowedOrigins}
                onChange={(e) => setAllowedOrigins(e.target.value)}
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground">
                Розділіть комами. Якщо пусто, використовується домен.
              </p>
            </div>

            {isEditing && (
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Статус</Label>
                  <p className="text-xs text-muted-foreground">
                    Вимкніть, щоб призупинити верифікації
                  </p>
                </div>
                <Switch checked={isActive} onCheckedChange={setIsActive} disabled={loading} />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Скасувати
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Збереження...
                </>
              ) : isEditing ? (
                'Зберегти'
              ) : (
                'Створити'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
